import React, {ReactNode} from 'react';

import {
  SaveToResultsStepDataName,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  getIconForView,
  getMainViewItemForView,
  getTitleForView,
  isLayoutView,
  isTopLevelAnalysisView,
  isLegacyTopLevelAnalysisView,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import {isStepInProgress} from 'analysis-tool/isAnalysisRunning';
import {getDocumentIdFromStep} from 'analysis-tool/outputs/OutputsUtil';
import SendToDocumentSidebarItemTitle from 'analysis-tool/outputs/SendToDocumentSidebarItemTitle';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  DataReference,
  StepInitializationAnalysisMainViewItem,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {
  allParentViewsSucceeded,
  getDefaultClientIdForView,
  getChildViewsForView,
  resolveLineageStep,
  resolveOutputStepData,
} from 'pipelines/PipelineViewUtils';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

import AddChartViewPopover from '../charting/AddChartViewPopover';
import {ViewSidebarOverrides} from './getViewSidebarOverrides';
import GoToFlowchartButton from './GoToFlowchartButton';
import InlineAddViewButton from './InlineAddViewButton';

export interface SidebarItemData {
  // If the icon should be duotone when selected, make sure it's a supported duotone icon in UI-Core Storybook
  // at https://duotone-icons--6303d99f6ffc62228a6a58f9.chromatic.com/ > Components > Icons > Duotone.
  // Otherwise it'll be rendered as a monotone icon. If it's not there, we'll need to add support for it by
  // requesting it in #design-systems.
  icon: BenchlingDuotoneIconName | BenchlingIconName;
  title: ReactNode;
  /** Main View item corresponding to this Sidebar item. Used for determining (a) which Main View item to
   * open when the Sidebar item is selected, and (b) if the Sidebar Item is already selected for rendering
   * purposes.
   */
  mainViewItem: AnalysisMainViewItem;
  childSidebarItems?: Array<SidebarItemData>;
  /**
   * Optional generator that produces inline action buttons for this sidebar item.
   * Called by the rendering layer with runtime dependencies (analysis, callbacks).
   * Populated during the data-generation walk in `getAnalysisViewSidebarItemData`.
   */
  generateActionButtons?: (
    analysis: PipelineGraphQL,
    onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void,
    onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void,
    onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void
  ) => Array<ReactNode>;
  /**
   * Optional options for the legacy block-level "Add view" button.
   * TODO(BNCH-145708): Remove once the legacy sidebar (behind `analysisEnableNewSidebar` flag) is deleted.
   */
  actionButtonOptions?: {
    stepDataReference: DataReference;
    dataFrameId: string;
  };
  subtitleText?: string;
  /**
   * Optional flag to mark an item as being disabled
   */
  disabled?: boolean;
  /**
   * Optional flag to mark that the item contains a FLAG_ROWS transformation
   */
  isFlaggingRows?: boolean;
  /**
   * Optional flag to mark an item as inactive, i.e. dissuade a user from clicking on it
   */
  inactive?: boolean;
  /**
   * Optional id for this sidebar item. Needed for collapsible sidebar items.
   *
   * For view-based items, this should be set to `getDefaultClientIdForView(view)`.
   */
  itemId?: string;
}

export const SIDEBAR_FAILED_ICON = 'exclamation-circle';

/** @package */
export const BASE_FAILED_SIDEBAR_ITEM_DATA: Pick<SidebarItemData, 'icon' | 'title'> = {
  icon: SIDEBAR_FAILED_ICON,
  title: 'Error',
};

export const SIDEBAR_EMPTY_ICON = 'exclamation-triangle';

export const SIDEBAR_SPINNER_ICON = 'spinner-third';

/** @package */
export const BASE_RUNNING_SIDEBAR_ITEM_DATA: Pick<SidebarItemData, 'icon' | 'title'> = {
  icon: SIDEBAR_SPINNER_ICON,
  title: 'Loading...',
};

export const SIDEBAR_HALTED_ICON = 'minus';

/** @package */
export const BASE_HALTED_SIDEBAR_ITEM_DATA: Pick<SidebarItemData, 'icon' | 'title'> = {
  icon: SIDEBAR_HALTED_ICON,
  title: 'Halted',
};

export const SIDEBAR_WAITING_ICON = 'circle-outline';

/** @package */
export const BASE_WAITING_SIDEBAR_ITEM_DATA: Pick<SidebarItemData, 'icon' | 'title'> = {
  icon: SIDEBAR_WAITING_ICON,
  title: 'Waiting to be configured',
};

function getFailedSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  return {
    ...BASE_FAILED_SIDEBAR_ITEM_DATA,
    mainViewItem: {
      type: AnalysisMainViewItemType.FAILED,
      stepId: step.id,
    },
  };
}

function getRunningSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  return {
    ...BASE_RUNNING_SIDEBAR_ITEM_DATA,
    mainViewItem: {
      type: AnalysisMainViewItemType.RUNNING,
      stepId: step.id,
    },
  };
}

function getHaltedSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  return {
    ...BASE_HALTED_SIDEBAR_ITEM_DATA,
    mainViewItem: {
      type: AnalysisMainViewItemType.HALTED,
      stepId: step.id,
    },
  };
}

function getWaitingSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  return {
    ...BASE_WAITING_SIDEBAR_ITEM_DATA,
    mainViewItem: {
      type: AnalysisMainViewItemType.RUNNING,
      stepId: step.id,
    },
  };
}

export function getSaveToResultsSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return {
      ...BASE_FAILED_SIDEBAR_ITEM_DATA,
      mainViewItem: {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId: step.id},
    };
  } else if (step.statusCode === PipelineStepStatusCode.HALTED) {
    return getHaltedSidebarItemData(step);
  } else if (step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    const inputTableName = step.inputStepDatas[0].dataFrames[0].name;
    return {
      icon: 'table',
      title: step.outputStepDatas.find(
        (stepData) => stepData.name === SaveToResultsStepDataName.SAVED_RESULTS
      )!.dataFrames[0].name,
      mainViewItem: {type: AnalysisMainViewItemType.SAVE_TO_RESULTS_OUTPUT, stepId: step.id},
      subtitleText: `From ${inputTableName} data`,
    };
  } else if (step.statusCode === PipelineStepStatusCode.WAITING) {
    return getWaitingSidebarItemData(step);
  } else {
    return getRunningSidebarItemData(step);
  }
}

export function getExportDataCatalogDatasetSidebarItemData(step: GetAnalysisPipelineStep): SidebarItemData {
  if (step.statusCode === PipelineStepStatusCode.FAILED) {
    return getFailedSidebarItemData(step);
  } else if (step.statusCode === PipelineStepStatusCode.HALTED) {
    return getHaltedSidebarItemData(step);
  } else if (step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    const outputDataFrame = step.outputStepDatas.find(
      (stepData) => stepData.name === SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
    )!.dataFrames[0];
    return {
      icon: 'dataset',
      title: outputDataFrame.name,
      mainViewItem: {
        type: AnalysisMainViewItemType.EXPORT_DATA_CATALOG_DATASET_OUTPUT,
        stepId: step.id,
        dataFrameId: outputDataFrame.id,
      },
    };
  } else if (step.statusCode === PipelineStepStatusCode.WAITING) {
    return getWaitingSidebarItemData(step);
  } else {
    return getRunningSidebarItemData(step);
  }
}

export function getSendToDocumentSidebarItemData(
  step: GetAnalysisPipelineStep,
  analysis?: PipelineGraphQL
): SidebarItemData {
  const documentId = getDocumentIdFromStep(step);
  const icon = APIUtil.apiIdToType(documentId) === ModelTypeConstants.WORKSHEET ? 'worksheet' : 'entry';

  let childSidebarItems: Array<SidebarItemData> | undefined;
  if (analysis != null) {
    childSidebarItems = getParentSteps(analysis, step)
      .filter((parentStep) =>
        [PipelineStepType.SAVE_TO_RESULTS, PipelineStepType.EXPORT_DATA_CATALOG_DATASET].includes(
          parentStep.definition.stepType
        )
      )
      .map((parentStep) =>
        parentStep.definition.stepType === PipelineStepType.SAVE_TO_RESULTS
          ? getSaveToResultsSidebarItemData(parentStep)
          : getExportDataCatalogDatasetSidebarItemData(parentStep)
      );
  }

  return {
    icon,
    title: <SendToDocumentSidebarItemTitle step={step} />,
    mainViewItem: {type: AnalysisMainViewItemType.SEND_TO_DOCUMENT, stepId: step.id},
    childSidebarItems:
      childSidebarItems != null && childSidebarItems.length > 0 ? childSidebarItems : undefined,
    itemId: step.id,
  };
}

/**
 * Returns item datas for outputs.
 *
 * The outputs section is laid out like this:
 *  - Output steps that lead to a SEND_TO_DOCUMENT are nested under the SEND_TO_DOCUMENT sidebar item
 *  - All other outputs are top level outputs
 *
 */
export function getOutputSidebarItemDatas(analysis: PipelineGraphQL): Array<SidebarItemData> {
  const legacySendToDocumentSteps = analysis.execution.steps.filter(
    ({definition: {stepType}}) => stepType === PipelineStepType.SEND_TO_DOCUMENT
  );

  const nestedParentStepIds = new Set(
    legacySendToDocumentSteps.flatMap((legacySendToDocStep) =>
      getParentSteps(analysis, legacySendToDocStep)
        .filter(
          ({definition: {stepType}}) =>
            stepType === PipelineStepType.SAVE_TO_RESULTS ||
            stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET
        )
        .map(({id}) => id)
    )
  );

  const standaloneResultItems = analysis.execution.steps
    .filter(
      ({id, definition: {stepType}}) =>
        stepType === PipelineStepType.SAVE_TO_RESULTS && !nestedParentStepIds.has(id)
    )
    .map((step) => getSaveToResultsSidebarItemData(step));

  const standaloneDatasetItems = analysis.execution.steps
    .filter(
      ({id, definition: {stepType}}) =>
        stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET && !nestedParentStepIds.has(id)
    )
    .map((step) => getExportDataCatalogDatasetSidebarItemData(step));

  const sendToDocumentItems = legacySendToDocumentSteps.map((step) =>
    getSendToDocumentSidebarItemData(step, analysis)
  );

  return [...standaloneResultItems, ...standaloneDatasetItems, ...sendToDocumentItems];
}

/**
 * Returns sidebar items for layout views (views whose last step is LAYOUT).
 *
 * Excludes layouts that are already placed in the sidebar via topology overrides (e.g. layouts
 * with a sidebarParent config), since those are nested under their parent view in "Analysis data".
 */
export function getLayoutSidebarItemDatas(
  analysis: PipelineGraphQL,
  viewSidebarOverrides?: ViewSidebarOverrides
): Array<SidebarItemData> {
  const skipViewIds = viewSidebarOverrides?.skipViewIds;
  return analysis.views
    .filter(
      (view) =>
        isLayoutView(view, analysis.steps) &&
        (skipViewIds == null || !skipViewIds.has(getDefaultClientIdForView(view, analysis.steps)))
    )
    .map((view) => getAnalysisViewSidebarItemData(view, analysis, viewSidebarOverrides));
}

/**
 * Returns the sidebar item data that is rooted with the given view.
 *
 * This should be used for the view-based behavior
 *
 * TODO(BNCH-125215): Support outputs directly here, without their own separate sections
 *
 * @param view The view to get sidebar item data for
 * @param analysis The analysis containing the view
 * @param viewSidebarOverrides Optional overrides for determining which child views to display
 * @returns The sidebar item data for the view
 */
export function getAnalysisViewSidebarItemData(
  view: PipelineViewGraphQL,
  analysis: PipelineGraphQL,
  viewSidebarOverrides?: ViewSidebarOverrides
): SidebarItemData {
  const viewId = getDefaultClientIdForView(view, analysis.steps);
  const {lineage} = view;
  const lastNode = lineage[lineage.length - 1];
  const isLayout = isLayoutView(view, analysis.steps);

  const icon = getIconForView(analysis, view);
  const title = getTitleForView(view, analysis.steps);

  const isFlaggingRows = view.lineage.some(
    (node) => resolveLineageStep(analysis.steps, node).stepType === PipelineStepType.FLAG_ROWS
  );

  const childViews = getChildViewsForView(analysis, view).filter((childView) => {
    const childViewId = getDefaultClientIdForView(childView, analysis.steps);
    return (
      !isTopLevelAnalysisView(childView, analysis) &&
      !isLegacyTopLevelAnalysisView(childView, analysis.steps) &&
      !viewSidebarOverrides?.skipViewIds.has(childViewId) &&
      (!viewSidebarOverrides?.excludeLayoutChildViews || !isLayoutView(childView, analysis.steps))
    );
  });

  const childOverrides = viewSidebarOverrides?.topologyOverrides.get(viewId) ?? [];
  const childSidebarItems = childViews
    .map((childView) => getAnalysisViewSidebarItemData(childView, analysis, viewSidebarOverrides))
    .concat(
      childOverrides.flatMap((childOverride) => {
        const childOverrideSidebarItems = childOverride.childViews.map((childView) =>
          getAnalysisViewSidebarItemData(childView, analysis, viewSidebarOverrides)
        );
        return childOverride.groupingItem != null
          ? {
              mainViewItem: getMainViewItemForView(childOverride.childViews[0], analysis.steps),
              childSidebarItems: childOverrideSidebarItems,
              itemId: `grouping-${getDefaultClientIdForView(childOverride.childViews[0], analysis.steps)}`,
              ...childOverride.groupingItem,
            }
          : childOverrideSidebarItems;
      })
    );

  const mainViewItem: AnalysisMainViewItem = getMainViewItemForView(view, analysis.steps);

  // Resolve step data for action button options
  const resolvedOutputStepData =
    lastNode.stepOutputReference != null
      ? resolveOutputStepData(analysis.steps, {
          step: lastNode.step,
          stepOutputReference: lastNode.stepOutputReference,
        })
      : null;

  const actionButtonOptions =
    lastNode.stepOutputReference != null &&
    lastNode.stepOutputReference.rawDataIndex != null &&
    resolvedOutputStepData != null &&
    resolvedOutputStepData.rawDatas[lastNode.stepOutputReference.rawDataIndex].__typename ===
      'DataFrameDomainNode'
      ? {
          stepDataReference: {
            stepDataName: resolvedOutputStepData.name,
            stepId: lastNode.step.id,
          },
          dataFrameId: resolvedOutputStepData.rawDatas[lastNode.stepOutputReference.rawDataIndex].id,
        }
      : undefined;

  const lineageStepIds = lineage.map((node) => node.step.id);

  function generateActionButtons(
    analysisArg: PipelineGraphQL,
    onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void,
    onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void,
    onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void
  ): Array<ReactNode> {
    const buttons: Array<ReactNode> = [
      <GoToFlowchartButton
        key="go-to-flowchart"
        onClick={() => {
          onChangeSelectedItem({
            type: AnalysisMainViewItemType.FLOWCHART,
            highlightedStepIds: [...lineageStepIds],
          });
        }}
      />,
    ];

    if (
      actionButtonOptions != null &&
      (analysisArg.permissions.writable || analysisArg.permissions.unarchivable)
    ) {
      buttons.push(
        <AddChartViewPopover
          key="add-view"
          analysis={analysisArg}
          stepId={actionButtonOptions.stepDataReference.stepId}
          dataFrameId={actionButtonOptions.dataFrameId}
          onOpenStepInitializationView={onOpenStepInitializationView}
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
        >
          {(openMenu: () => void) => (
            <InlineAddViewButton
              disabledReason={(() => {
                const step = analysisArg.execution.steps.find(
                  (s) => s.id === actionButtonOptions.stepDataReference.stepId
                );
                if (step != null && isStepInProgress(step)) {
                  return 'View is still running';
                }
                if (isArchived(analysisArg) && analysisArg.permissions.unarchivable) {
                  return getArchivedTooltipMessage(analysisArg);
                }
                return null;
              })()}
              onClick={openMenu}
            />
          )}
        </AddChartViewPopover>
      );
    }

    return buttons;
  }

  return {
    icon,
    title,
    isFlaggingRows,
    // Mark views with a failed parent view as inactive, to indicate to the user that opening the view is
    // inactionable. We don't do this for layouts since layouts may control the status of their parent views.
    inactive: !allParentViewsSucceeded(analysis, view) && !isLayout,
    mainViewItem,
    generateActionButtons,
    // TODO(BNCH-145708): Remove actionButtonOptions once the legacy sidebar is deleted.
    actionButtonOptions: isTopLevelAnalysisView(view, analysis) ? actionButtonOptions : undefined,
    childSidebarItems,
    itemId: viewId,
  };
}
